/**
 * Shared constants for the Leafstock app
 */

// Built-in categories (these always exist as defaults)
export const BUILT_IN_CATEGORIES = [
  'FLOWER',
  'PREROLL',
  'CONCENTRATE',
  'EDIBLE',
  'VAPE',
  'TOPICAL',
  'ACCESSORY',
  'DRINKS',
] as const;

// Category icons used across Reports components
export const categoryIcons: Record<string, string> = {
  FLOWER: '🌿',
  AAA_FLOWER: '🌿',
  GREENDOOR_FLOWER: '🌿',
  PREROLL: '🚬',
  CONCENTRATE: '💎',
  EDIBLE: '🍫',
  VAPE: '💨',
  TOPICAL: '🧴',
  ACCESSORY: '🔧',
  DRINKS: '🥤',
};

// Category display names
export const categoryNames: Record<string, string> = {
  FLOWER: 'Flower',
  AAA_FLOWER: 'AAA Flower (Premium)',
  GREENDOOR_FLOWER: 'Greendoor (High)',
  PREROLL: 'Pre-rolls',
  CONCENTRATE: 'Concentrates',
  EDIBLE: 'Edibles',
  VAPE: 'Vapes',
  TOPICAL: 'Topicals',
  ACCESSORY: 'Accessories',
  DRINKS: 'Drinks',
};

// Category colors for UI styling
export const categoryColors: Record<string, string> = {
  FLOWER: 'border-green-500/30 bg-green-500/5',
  PREROLL: 'border-amber-500/30 bg-amber-500/5',
  CONCENTRATE: 'border-blue-500/30 bg-blue-500/5',
  EDIBLE: 'border-purple-500/30 bg-purple-500/5',
  VAPE: 'border-cyan-500/30 bg-cyan-500/5',
  TOPICAL: 'border-pink-500/30 bg-pink-500/5',
  ACCESSORY: 'border-orange-500/30 bg-orange-500/5',
  DRINKS: 'border-red-500/30 bg-red-500/5',
};

/**
 * Get category icon with fallback for custom categories
 */
export function getCategoryIcon(category: string): string {
  return categoryIcons[category] || '📦';
}

/**
 * Get category color with fallback for custom categories
 */
export function getCategoryColor(category: string): string {
  return categoryColors[category] || 'border-gray-500/30 bg-gray-500/5';
}

/**
 * Get category display name with fallback for custom categories
 */
export function getCategoryName(category: string): string {
  return categoryNames[category] || category;
}

/**
 * Get default unit type for a category
 */
export function getDefaultUnitType(category: string): 'GRAMS' | 'UNITS' | 'ML' {
  if (['FLOWER', 'CONCENTRATE', 'PREROLL'].includes(category)) return 'GRAMS';
  if (category === 'VAPE') return 'ML';
  return 'UNITS';
}

// Re-export variance thresholds from shared package
export { VARIANCE_THRESHOLDS as DISCREPANCY_THRESHOLDS, isAccumulatingProduct } from '@leafstock/shared';

// Import the shared function and re-export with legacy naming
import { getVarianceStatus, isAccumulatingProduct as _isAccumulating } from '@leafstock/shared';

/**
 * Get discrepancy status based on variance value
 * Maps shared function to legacy 'good' | 'warning' | 'critical' return values
 * @param variance - The variance/discrepancy value in grams
 * @param productName - Optional product name to check for exemptions (e.g., STEMS)
 * @returns 'good' | 'warning' | 'critical'
 */
export function getDiscrepancyStatus(
  variance: number,
  productName?: string
): 'good' | 'warning' | 'critical' {
  const status = getVarianceStatus(variance, productName);
  // Map ACCEPTABLE/WARNING/CRITICAL to good/warning/critical
  return status === 'ACCEPTABLE' ? 'good' : status === 'WARNING' ? 'warning' : 'critical';
}
